import PopUpBase from '@components/PopUpBase';
import StyledAutocomplete from '@components/styled-autocomplete';
import { IPopUp } from '@hooks/usePopUp';
import { LoadingButton } from '@mui/lab';
import { Box, Button, CircularProgress, Divider, Skeleton, Stack, Typography } from '@mui/material';
import ListNoData from '@pages/cameras/list/components/NoDataCameras';
import color from '@theme/Colors';
import Konva from 'konva';
import { FC, useEffect, useRef, useState } from 'react';
import { Image, Layer, Stage } from 'react-konva';
import { PREVIEW_RATIO, useCameraDetailContext } from '../../../../../hook/CameraDetailsProvider';
import { IGetPhotoSnapShotByPTZ } from '@EcamModel/controllers/ICameraViewHttpController';
import { cameraViewController } from '@controllers/index';
import { pushError } from '@components/toast';
import { CameraViewType, ViewPTZ, ViewPreset } from '@EcamModel/model/CameraView';
import { Filter } from '@pages/cameras/list';
import { ListProps } from '@Core';
import { TypeReturn } from '@components/styled-autocomplete/useStyledAutocomplete';

type Props = Omit<IPopUp, 'onConfirm'> & {
    filter: Filter;
    listsViewPresetAutocomplete: TypeReturn<ViewPreset, ListProps<ViewPreset>>;
    listsViewPreset: ViewPreset[];
    onSetFilter: (filter: Filter | ((prev: Filter) => Filter)) => void;
};

export const RESOLUTIONS = {
    HD: { width: 1280, height: 720 },
    FULL_HD: { width: 1920, height: 1080 },
    '2K': { width: 2560, height: 1440 },
    '4K': { width: 3840, height: 2160 },
} as const;

type ResolutionType = keyof typeof RESOLUTIONS;

export const base64ToHTMLImageElement = (
    base64: string,
    targetResolution: ResolutionType = 'FULL_HD'
): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
        const img = new window.Image();

        img.onload = () => {
            const targetWidth = RESOLUTIONS[targetResolution].width;
            const targetHeight = RESOLUTIONS[targetResolution].height;

            const canvas = document.createElement('canvas');
            canvas.width = targetWidth;
            canvas.height = targetHeight;
            const ctx = canvas.getContext('2d');

            if (ctx) {
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';

                ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

                const resizedBase64 = canvas.toDataURL('image/jpeg', 0.95);
                const resizedImg = new window.Image();
                resizedImg.src = resizedBase64;
                resizedImg.onload = () => resolve(resizedImg);
                resizedImg.onerror = (err) => reject(err);
            } else {
                reject('Error loading canvas context');
            }
        };

        img.onerror = (err) => reject(err);
        img.src = `data:image/jpeg;base64,${base64}`;
    });
};
export const PREVIEW_WIDTH = 852;
const PopupConfigViewCameraPTZ: FC<Props> = (props) => {
    const { open, onClose, filter, listsViewPreset, listsViewPresetAutocomplete, onSetFilter } = props;

    const {
        camera,
        handleUpsertView,
        filteredCamera,
        isLoadingSave,
        photoSnapShotByPTZ,
        setPhotoSnapShotByPTZ,
        selectedCameraView,
    } = useCameraDetailContext();

    const stageRef = useRef<Konva.Stage>(null);
    const [imageElement, setImageElement] = useState<HTMLImageElement | null>(null);
    const initialPerspectiveViewRef = useRef<Partial<ViewPTZ> | null>(null);
    const shouldSkipApiCall = useRef<Partial<ViewPTZ> | boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const cameraViewName = selectedCameraView?.CameraViewName;

    const scaleSize = PREVIEW_WIDTH / (imageElement?.width ?? 1920);

    const handleClosePopup = () => {
        onClose?.();
    };

    useEffect(() => {
        if (photoSnapShotByPTZ) {
            const convertBase64ToImage = async () => {
                try {
                    const imgElement = await base64ToHTMLImageElement(photoSnapShotByPTZ.Base64PhotoSnapShot);
                    setImageElement(imgElement);
                } catch (error) {
                    console.error('Failed to convert base64 to image', error);
                }
            };
            convertBase64ToImage();
        }
    }, [photoSnapShotByPTZ]);

    const handleResetFilter = () => {
        const newPerspectiveView: Partial<ViewPreset> = {
            GotoPointId: listsViewPreset?.[0]?.GotoPointId,
            PtzPanPos: listsViewPreset?.[0]?.PtzPanPos,
            PtzTiltPos: listsViewPreset?.[0]?.PtzTiltPos,
            PtzZoomPos: listsViewPreset?.[0]?.PtzZoomPos,
            PresetName: listsViewPreset?.[0]?.PresetName,
        };

        onSetFilter((prev) => ({
            ...prev,
            perspectiveView: newPerspectiveView,
        }));

        if (!initialPerspectiveViewRef.current) {
            initialPerspectiveViewRef.current = newPerspectiveView;
        }
    };

    useEffect(() => {
        if (!open) return;
        handleResetFilter();

        shouldSkipApiCall.current = false;
    }, [open]);

    useEffect(() => {
        if (!initialPerspectiveViewRef.current) return;

        const PTZ =
            filter.perspectiveView?.PtzPanPos !== undefined ||
            filter.perspectiveView?.PtzZoomPos !== undefined ||
            filter.perspectiveView?.PtzTiltPos !== undefined
                ? {
                      PtzPanPos: filter.perspectiveView?.PtzPanPos,
                      PtzZoomPos: filter.perspectiveView?.PtzZoomPos,
                      PtzTiltPos: filter.perspectiveView?.PtzTiltPos,
                  }
                : undefined;

        if (PTZ || filter.perspectiveView?.GotoPointId !== undefined) {
            getPhotoSnapShotByView({
                PTZ:
                    filter.perspectiveView?.GotoPointId !== undefined
                        ? {
                              GotoPointId: filter.perspectiveView?.GotoPointId,
                          }
                        : PTZ!,
                PTZCommand: filteredCamera,
            });
            shouldSkipApiCall.current = true;
        }
    }, [filter.perspectiveView]);

    const getPhotoSnapShotByView = async (_filter: IGetPhotoSnapShotByPTZ) => {
        setIsLoading(true);
        await cameraViewController
            .getPhotoSnapShotByPTZ(_filter)
            .then((res) => {
                setPhotoSnapShotByPTZ(res);
            })
            .catch((err) => {
                pushError(err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const conditionDisabled =
        isLoadingSave || isLoading || !imageElement || filter.perspectiveView?.GotoPointId === undefined;
    const title = `Add new action for camera: ${camera?.Name}`;

    return (
        <PopUpBase
            open={open}
            fixOverflow
            onClose={onClose}
            dialogProps={{
                fullWidth: true,
                sx: {
                    '& .MuiDialog-paper': {
                        borderRadius: '10px',
                    },
                    '& .MuiDialogContent-root': {
                        padding: '0px 24px',
                    },
                    '& .MuiPaper-root': {
                        overflowY: 'unset',
                    },
                    zIndex: (theme) => theme.zIndex.modal,
                },
                maxWidth: 'md',
            }}
            title={<Typography variant="h4">{title}</Typography>}
            titleProps={{ '&.MuiDialogTitle-root': { paddingTop: '24px' } }}
            subTitleProps={{ sx: { color: 'gray' } }}
            minWidthButton={150}
            desc={
                <Stack direction={'column'} spacing={2} width={'100%'}>
                    <Stack width={'100%'}>
                        <StyledAutocomplete
                            {...listsViewPresetAutocomplete}
                            getOptionLabel={(o) => o.PresetName ?? ''}
                            label="View"
                            isRequired
                            disabled={isLoading}
                            value={filter.perspectiveView}
                            disabledAllOption
                            onChange={(o) => {
                                onSetFilter((prev) => ({ ...prev, perspectiveView: o }));
                            }}
                        />
                    </Stack>

                    <Divider orientation="horizontal" flexItem sx={{ borderColor: color.grey300 }} />

                    {filter.perspectiveView?.GotoPointId !== undefined ||
                    filter.perspectiveView?.PtzPanPos !== undefined ? (
                        <Box>
                            {!imageElement || isLoading ? (
                                <Skeleton
                                    width="100%"
                                    sx={{
                                        borderRadius: '4px',
                                        bgcolor: color.grey200,
                                        minHeight: PREVIEW_WIDTH * PREVIEW_RATIO,
                                        width: '100%',
                                    }}
                                    variant="rectangular"
                                />
                            ) : (
                                <Stage ref={stageRef} width={PREVIEW_WIDTH} height={PREVIEW_WIDTH * PREVIEW_RATIO}>
                                    <Layer>
                                        <Image image={imageElement} scaleX={scaleSize} scaleY={scaleSize} />
                                    </Layer>
                                </Stage>
                            )}
                        </Box>
                    ) : (
                        <ListNoData
                            sx={{
                                minHeight: '300px',
                                backgroundColor: color.white,
                                border: 'none',
                            }}
                        />
                    )}
                </Stack>
            }
            customActions={
                <Stack mt={3} direction={'row'} width="100%" justifyContent="space-between">
                    <Button sx={{ minWidth: 130 }} variant="cancel" onClick={handleClosePopup} disabled={isLoadingSave}>
                        Cancel
                    </Button>
                    <LoadingButton
                        sx={{
                            minWidth: 130,
                        }}
                        loading={isLoadingSave}
                        loadingPosition="center"
                        variant="contained"
                        disabled={conditionDisabled}
                        onClick={() => {
                            handleUpsertView(filter, CameraViewType.ViewOverview);
                        }}
                    >
                        Save
                    </LoadingButton>
                </Stack>
            }
        />
    );
};
export default PopupConfigViewCameraPTZ;
